/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
    Workflow,
    WorkflowObjectState,
    Query,
    Predicate,
    CloudObject,
    ErrorFlow,
    Order,
    tagToString,
    Transaction,
    User,
    Brick,
    Auth,
    WorkflowTransition,
} from 'olympe';

/**
 * @param {!Transaction} t
 * @param {!CloudObject} object
 * @param {!Workflow} workflow
 * @return {!Promise}
 */
export async function initialize(t, object, workflow) {
    // Check inputs
    if(!object || !workflow) {
        throw new Error(`inputs 'object' and 'workflow' should not be null`);
    }

    // Check if the object model is associated with the workflow
    const workflowDataType = workflow.followSingle(Workflow.dataTypeRel).executeFromCache();
    if(!workflowDataType || tagToString(workflowDataType) !== object.getModelTag()) {
        throw new Error(`selected workflow (${workflow.name()}) is not associated with the object model (${object.getModel().name()})`);
    }

    // Check that the object is not already using the workflow
    const objectStates = await object.follow(WorkflowObjectState.objectRel.getInverse()).execute();
    const alreadyInitialized = objectStates.size() > 0;
    if (alreadyInitialized) {
        throw new Error(`There is already a workflow initialized on this object ('${object.getTag()}')`);
    }

    // Initialize the workflow on the object
    const initialState = workflow.followSingle(Workflow.initialStateRel).executeFromCache();
    return setState(t, object, initialState, false);
}

/**
 * @param {!BrickContext} $
 * @param {!Transaction} t
 * @param {!CloudObject} object
 * @param {!WorkflowTransition} transition
 * @param {Map} data
 * @return {!Promise}
 */
export async function triggerTransition($, t, object, transition, data) {
    // Check inputs
    if(!object || !transition) {
        throw new Error(`inputs 'object' and 'transition' should not be null`);
    }

    // Check if the object uses the workflow
    const fromState = transition.followSingle(WorkflowTransition.fromStateRel).executeFromCache();
    const workflow = fromState.followSingle(Workflow.statesRel.getInverse()).executeFromCache();
    const objectStates = await object.follow(WorkflowObjectState.objectRel.getInverse())
        .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC)
        .filter(Predicate.equals(WorkflowObjectState.workflowProp, workflow.name()))
        .execute();
    const currentObjectState = objectStates.getFirst();
    if(!currentObjectState) {
        throw new Error(`the object is not initialized with this transition workflow (${workflow.name()})`);
    }

    // Check if the object is in the right state for the transition
    if(currentObjectState.get(WorkflowObjectState.stateProp) !== fromState.name()) {
        const currentObjectStateName = currentObjectState.get(WorkflowObjectState.stateProp);
        throw new Error(`the object current state (${currentObjectStateName}) is not valid for the selected transition (${transition.name()})`);
    }

    // Change state common logic
    const doTransition = () => {
        const toState = transition.followSingle(WorkflowTransition.toStateRel).executeFromCache();
        return setState(t, object, toState, true);
    };

    // If the transition has a process function -> execute it
    const processRef = transition.get(WorkflowTransition.processRefProp);
    if (processRef) {
        // Execute the process function
        const process = Brick.get(processRef);
        const [controlFlowInput, objectInput, userInput, dataInput] = process.getInputs();
        const [controlFlowOutput, errorFlowOutput] = process.getOutputs();
        const process$ = $.runner(process)
            .set(objectInput, object)
            .set(userInput, CloudObject.get(Auth.getCurrentUser()))
            .set(dataInput, data)
            .trigger(controlFlowInput);

        // Wait for either the control flow or the error flow
        return Promise.any([
            process$.waitFor(controlFlowOutput),
            process$.waitFor(errorFlowOutput)
        ]).then(error => {
            // Destroy the context processing the transition
            process$.destroy();
            // If the error flow was triggered
            if(error instanceof ErrorFlow) {
                throw new Error(error.message);
            }
            // If the process function succeed -> change state
            else {
                return doTransition();
            }
        })
    }

    // Else change state directly
    else {
        return doTransition();
    }
}


/**
 * @param {!Query} query
 * @param {!WorkflowObjectState} state
 * @param {boolean} not
 * @return {!Query}
 */
export function queryFilterByState(query, state, not) {
    const statePredicate = not 
        ? Predicate.not(Predicate.equals(WorkflowObjectState.stateProp, state.name())) 
        : Predicate.equals(WorkflowObjectState.stateProp, state.name());
    return query.andReturn()
        .follow(WorkflowObjectState.currentObjectRel.getInverse())
        .filter(statePredicate);
}

/**
 * @param {!CloudObject} object
 * @param {!Date} date
 * @return {!Query}
 */
export function queryHistoryAtDate(object, date) {
    return object.follow(WorkflowObjectState.objectRel.getInverse())
        .filter(Predicate.smallerThan(WorkflowObjectState.dateTimeProp, date, false))
        .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC);
}

/**
 * @protected
 * @param {!CloudObject} object
 * @param {!WorkflowObjectState} state
 * @return {!Query}
 */
export function queryHistoryAtState(object, state) {
    return object.follow(WorkflowObjectState.objectRel.getInverse())
        .filter(Predicate.equals(WorkflowObjectState.stateProp, state.name()))
        .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC);
}

/**
 * @param {!CloudObject} object
 * @return {!Query}
 */
export function queryObjectHistory(object) {
    return object.follow(WorkflowObjectState.objectRel.getInverse())
        .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC);
}

/**
 * @param {!CloudObject} object
 * @return {Query<WorkflowObjectState>}
 */
export function queryObjectState(object) {
    return object.follow(WorkflowObjectState.currentObjectRel.getInverse());
}

/**
 * @param {!CloudObject} object
 * @param {!WorkflowObjectState} state
 * @return {!Promise<boolean>}
 */
export async function hasStateBeenReached(object, state) {
    const qr = await queryHistoryAtState(object, state).execute();
    return qr.size() > 0;
}

/**
 * @param {!CloudObject} object
 * @param {!WorkflowState} state
 * @return {!Promise<boolean>} 
 */
export async function isObjectInState(object, state) {
    const workflow = state.followSingle(Workflow.statesRel.getInverse()).executeFromCache();
    const qr = await object.follow(WorkflowObjectState.objectRel.getInverse())
        .sortBy(WorkflowObjectState.dateTimeProp, Order.DESC)
        .filter(Predicate.equals(WorkflowObjectState.workflowProp, workflow.name()))
        .limit(1)
        .execute();
    const first = qr.getFirst();
    return first && state.name() === first.get(WorkflowObjectState.stateProp);
}

/**
 * @param {!Transaction} t
 * @param {!CloudObject} object
 * @param {!WorkflowState} state
 * @param {boolean=} isTransition
 * @return {!Promise}
 */
async function setState(t, object, state, isTransition = true) {
    // Find the workflow
    const workflow = state.followSingle(Workflow.statesRel.getInverse()).executeFromCache();

    // Set the state properties
    const currentUserTag = Auth.getCurrentUser();
    const currentUser = CloudObject.get(currentUserTag);
    const assignee = currentUser.get(User.SAMLnameIdProp) ?? currentUser.get(User.loginProp);
    const properties = new Map()
        .set(WorkflowObjectState.workflowProp, workflow.name())
        .set(WorkflowObjectState.stateProp, state.name())
        .set(WorkflowObjectState.assigneeProp, assignee)
        .set(WorkflowObjectState.dateTimeProp, new Date());

    // If serialization is enabled -> save the serialized object
    if(workflow.get(Workflow.serializationEnabledProp)) {
        const serializedObject = JSON.stringify(object.toObject(true, false));
        properties.set(WorkflowObjectState.serializedObjectProp, serializedObject);
    }

    // Create the new object state
    const objectState = t.create(WorkflowObjectState, properties, object.getSource());

    // Set persistence from object
    t.persist(objectState, object.isPersisted());
    t.createRelation(WorkflowObjectState.objectRel, objectState, object);
    if (isTransition) {
        const currentState = await Query.from(object).follow(WorkflowObjectState.currentObjectRel.getInverse()).execute();
        t.deleteRelation(WorkflowObjectState.currentObjectRel, currentState.getFirst(), object);
    }
    t.createRelation(WorkflowObjectState.currentObjectRel, objectState, object);
}

